import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  Video,
  PhoneOff,
  Clock,
  Users,
  AlertTriangle,
  Search,
  RefreshCw,
  Wifi,
  Eye,
} from 'lucide-react';
import MainLayout from '../components/layout/MainLayout';
import { Badge, Button, Card, Input, Modal, Skeleton } from '../components/ui';
import type { BadgeVariant } from '../components/ui';
import { useCollection } from '../hooks/useFirestore';
import { useToast } from '../contexts/ToastContext';
import { functions } from '../config/firebase';
import type { Consulta, DisposicionConsulta } from '../types';
import styles from './SesionesPage.module.css';

const activaConstraints = [where('estado', '==', 'en_atencion')];

const cerrarSesionAdminFn = httpsCallable(functions, 'portal_cerrarSesionAdmin');
const limpiarRoomsFn = httpsCallable(functions, 'portal_limpiarRoomsHuerfanos');

const DISPOSITION_OPTIONS: { value: DisposicionConsulta; label: string }[] = [
  { value: 'completada', label: 'Consulta completada' },
  { value: 'fallo_tecnico', label: 'Fallo tecnico' },
  { value: 'cancelada_medico', label: 'Cancelada por el medico' },
  { value: 'incompleta', label: 'Consulta incompleta' },
];

function getElapsedTime(iniciadaEn: any): string {
  if (!iniciadaEn || typeof iniciadaEn.toDate !== 'function') return '—';
  const start = iniciadaEn.toDate().getTime();
  const now = Date.now();
  const diffSec = Math.floor((now - start) / 1000);
  const h = Math.floor(diffSec / 3600);
  const m = Math.floor((diffSec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function getDurationBadgeVariant(iniciadaEn: any): BadgeVariant {
  if (!iniciadaEn || typeof iniciadaEn.toDate !== 'function') return 'neutral';
  const diffMin = (Date.now() - iniciadaEn.toDate().getTime()) / 60000;
  if (diffMin < 15) return 'success';
  if (diffMin < 30) return 'warning';
  return 'error';
}

// ── Orphaned rooms types ──
interface RoomReport {
  roomName: string;
  numParticipants: number;
  consultaEstado: string | null;
  shouldClose: boolean;
  reason: string;
  closed?: boolean;
}

interface OrphanedResult {
  summary: {
    totalRooms: number;
    hueérfanos: number;
    activos: number;
    cerrados: number;
    apply: boolean;
  };
  reports: RoomReport[];
}

export default function SesionesPage() {
  const { data, loading, error } = useCollection<Consulta>('consultas', activaConstraints);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [closingId, setClosingId] = useState<string | null>(null);
  const [selectedConsulta, setSelectedConsulta] = useState<Consulta | null>(null);
  const [disposition, setDisposition] = useState<DisposicionConsulta>('completada');
  const [isClosing, setIsClosing] = useState(false);

  // Orphaned rooms state
  const [orphanedRooms, setOrphanedRooms] = useState<RoomReport[]>([]);
  const [orphanedLoading, setOrphanedLoading] = useState(false);
  const [orphanedChecked, setOrphanedChecked] = useState(false);
  const [closingRoom, setClosingRoom] = useState<string | null>(null);

  // Auto-refresh elapsed time every 30s
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!searchTerm.trim()) return data;
    const term = searchTerm.toLowerCase();
    return data.filter(
      (c) =>
        c.pacienteNombre.toLowerCase().includes(term) ||
        c.salaNombre.toLowerCase().includes(term) ||
        (c.medicoNombre?.toLowerCase().includes(term) ?? false),
    );
  }, [data, searchTerm]);

  const handleOpenClose = useCallback((consulta: Consulta) => {
    setSelectedConsulta(consulta);
    setDisposition('completada');
  }, []);

  const handleConfirmClose = useCallback(async () => {
    if (!selectedConsulta) return;
    setIsClosing(true);
    setClosingId(selectedConsulta.id);
    try {
      await cerrarSesionAdminFn({
        consultaId: selectedConsulta.id,
        disposicion: disposition,
      });
      addToast('success', `Sesion de ${selectedConsulta.pacienteNombre} cerrada correctamente`);
    } catch (err: any) {
      const msg = err?.message || 'Error al cerrar la sesion';
      addToast('error', msg);
    } finally {
      setIsClosing(false);
      setClosingId(null);
      setSelectedConsulta(null);
    }
  }, [selectedConsulta, disposition, addToast]);

  // ── Orphaned rooms handlers ──
  const handleCheckOrphaned = useCallback(async () => {
    setOrphanedLoading(true);
    try {
      const res = await limpiarRoomsFn({ apply: false });
      const result = res.data as OrphanedResult;
      setOrphanedRooms(result.reports.filter((r) => r.shouldClose));
      setOrphanedChecked(true);
    } catch (err: any) {
      addToast('error', err?.message || 'Error al consultar rooms LiveKit');
    } finally {
      setOrphanedLoading(false);
    }
  }, [addToast]);

  const handleCloseOrphaned = useCallback(async (roomName: string) => {
    setClosingRoom(roomName);
    try {
      await limpiarRoomsFn({ apply: true });
      setOrphanedRooms((prev) => prev.filter((r) => r.roomName !== roomName));
      addToast('success', `Room ${roomName} cerrado`);
    } catch (err: any) {
      addToast('error', err?.message || 'Error al cerrar room');
    } finally {
      setClosingRoom(null);
    }
  }, [addToast]);

  const handleCloseAllOrphaned = useCallback(async () => {
    setOrphanedLoading(true);
    try {
      const res = await limpiarRoomsFn({ apply: true });
      const result = res.data as OrphanedResult;
      addToast('success', `${result.summary.cerrados} room(s) huerfano(s) cerrado(s)`);
      setOrphanedRooms([]);
    } catch (err: any) {
      addToast('error', err?.message || 'Error al cerrar rooms');
    } finally {
      setOrphanedLoading(false);
    }
  }, [addToast]);

  const totalActivas = data?.length ?? 0;

  return (
    <MainLayout pageTitle="Sesiones Activas">
      <p className={styles.description}>
        Sesiones de videollamada actualmente en curso. Puede cerrar sesiones desde aqui.
      </p>

      {/* KPI */}
      {!loading && (
        <div className={styles.kpiGrid}>
          <Card className={styles.kpiCard}>
            <div className={styles.kpiIcon}>
              <Video size={20} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiValue}>{totalActivas}</span>
              <span className={styles.kpiLabel}>
                {totalActivas === 1 ? 'Sesion activa' : 'Sesiones activas'}
              </span>
            </div>
          </Card>
          {orphanedChecked && orphanedRooms.length > 0 && (
            <Card className={`${styles.kpiCard} ${styles.kpiCardWarning}`}>
              <div className={`${styles.kpiIcon} ${styles.kpiIconWarning}`}>
                <Wifi size={20} />
              </div>
              <div className={styles.kpiContent}>
                <span className={styles.kpiValue}>{orphanedRooms.length}</span>
                <span className={styles.kpiLabel}>Rooms huerfanos</span>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Search + Orphaned check */}
      {!loading && (
        <div className={styles.toolbar}>
          {totalActivas > 0 && (
            <div className={styles.searchWrapper}>
              <Input
                leftIcon={<Search size={18} />}
                placeholder="Buscar por paciente, sala o medico..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={handleCheckOrphaned}
            disabled={orphanedLoading}
          >
            <RefreshCw size={14} className={orphanedLoading ? styles.spinning : ''} />
            {orphanedLoading ? 'Verificando...' : 'Verificar LiveKit'}
          </Button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className={styles.loadingGrid}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="card" height={140} />
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className={styles.errorState}>
          <AlertTriangle size={24} />
          <p>Error al cargar sesiones: {error.message}</p>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && totalActivas === 0 && !orphanedChecked && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <Users size={36} />
          </div>
          <p className={styles.emptyMessage}>
            No hay sesiones activas en este momento.
            Use el boton "Verificar LiveKit" para detectar rooms huerfanos.
          </p>
        </div>
      )}

      {/* Session cards */}
      {!loading && !error && filtered.length > 0 && (
        <div className={styles.grid}>
          {filtered.map((consulta) => (
            <Card key={consulta.id} className={styles.sessionCard}>
              <div className={styles.sessionHeader}>
                <div className={styles.sessionInfo}>
                  <h3 className={styles.patientName}>{consulta.pacienteNombre}</h3>
                  <span className={styles.roomName}>{consulta.salaNombre}</span>
                </div>
                <Badge variant={getDurationBadgeVariant(consulta.iniciadaEn)} size="sm">
                  <Clock size={12} />
                  {getElapsedTime(consulta.iniciadaEn)}
                </Badge>
              </div>

              <div className={styles.sessionBody}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Motivo:</span>
                  <span className={styles.detailValue}>{consulta.motivoConsulta}</span>
                </div>
                {consulta.medicoNombre && (
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Medico:</span>
                    <span className={styles.detailValue}>{consulta.medicoNombre}</span>
                  </div>
                )}
              </div>

              <div className={styles.sessionFooter}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(`/consulta/${consulta.id}/observar`)}
                  title="Unirse como observador a esta teleconsulta"
                >
                  <Eye size={14} />
                  Observar
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleOpenClose(consulta)}
                  disabled={closingId === consulta.id}
                >
                  <PhoneOff size={14} />
                  {closingId === consulta.id ? 'Cerrando...' : 'Cerrar sesion'}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* ── Orphaned rooms section ── */}
      {orphanedChecked && orphanedRooms.length > 0 && (
        <div className={styles.orphanedSection}>
          <div className={styles.orphanedHeader}>
            <h3 className={styles.orphanedTitle}>
              <Wifi size={18} />
              Rooms huerfanos en LiveKit
            </h3>
            <Button
              variant="danger"
              size="sm"
              onClick={handleCloseAllOrphaned}
              disabled={orphanedLoading}
            >
              Cerrar todos ({orphanedRooms.length})
            </Button>
          </div>
          <p className={styles.orphanedDesc}>
            Estos rooms estan activos en LiveKit pero su consulta ya fue finalizada o no existe.
          </p>
          <div className={styles.grid}>
            {orphanedRooms.map((room) => (
              <Card key={room.roomName} className={`${styles.sessionCard} ${styles.orphanedCard}`}>
                <div className={styles.sessionHeader}>
                  <div className={styles.sessionInfo}>
                    <h3 className={styles.patientName}>{room.roomName}</h3>
                    <span className={styles.roomName}>
                      {room.numParticipants} participante(s)
                    </span>
                  </div>
                  <Badge variant="error" size="sm">Huerfano</Badge>
                </div>
                <div className={styles.sessionBody}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Estado:</span>
                    <span className={styles.detailValue}>
                      {room.consultaEstado || 'No existe'}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Razon:</span>
                    <span className={styles.detailValue}>{room.reason}</span>
                  </div>
                </div>
                <div className={styles.sessionFooter}>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleCloseOrphaned(room.roomName)}
                    disabled={closingRoom === room.roomName}
                  >
                    <PhoneOff size={14} />
                    {closingRoom === room.roomName ? 'Cerrando...' : 'Cerrar room'}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {orphanedChecked && orphanedRooms.length === 0 && (
        <div className={styles.orphanedClean}>
          No se encontraron rooms huerfanos en LiveKit.
        </div>
      )}

      {/* Close confirmation modal */}
      <Modal
        open={!!selectedConsulta}
        onClose={() => !isClosing && setSelectedConsulta(null)}
        title="Confirmar cierre de sesion"
      >
        <div className={styles.modalContent}>
          <div className={styles.modalWarning}>
            <AlertTriangle size={20} />
            <p>
              Esta a punto de cerrar la sesion de{' '}
              <strong>{selectedConsulta?.pacienteNombre}</strong>.
              Esta accion finalizara la videollamada y liberara al medico.
            </p>
          </div>

          <div className={styles.dispositionSection}>
            <label className={styles.dispositionLabel}>Motivo de cierre:</label>
            <div className={styles.dispositionGrid}>
              {DISPOSITION_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`${styles.dispositionOption} ${
                    disposition === opt.value ? styles.dispositionOptionActive : ''
                  }`}
                  onClick={() => setDisposition(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.modalActions}>
            <Button
              variant="secondary"
              onClick={() => setSelectedConsulta(null)}
              disabled={isClosing}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmClose}
              disabled={isClosing}
            >
              {isClosing ? 'Cerrando...' : 'Confirmar cierre'}
            </Button>
          </div>
        </div>
      </Modal>
    </MainLayout>
  );
}
